import { useUser, useTotalStaff } from "../Use-auth";
import { Calendar, Upload, X, Menu } from "lucide-react";
import { useDateRange } from "../Pages/DateRangeContext";
import Notifications from "../Pages/Notifications";
import { useLocation } from "wouter";
import { useState, useEffect, useRef } from "react";

function formatDate(date) {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

export default function Dashboard({ setOpen }) {
    const { data: user } = useUser();
    const { data: total } = useTotalStaff();

    const {
        rangeType,
        setRangeType,
        start,
        end,
        selectedYear,
        setSelectedYear,
        selectedDate,
        setSelectedDate,
    } = useDateRange();

    const [location] = useLocation();
    const [isMobile, setIsMobile] = useState(false);
    const [showCalendar, setShowCalendar] = useState(false);
    const calendarRef = useRef(null);
    const [profileImage, setProfileImage] = useState(null);

    useEffect(() => {
        if (user && total) {
            const currentUserFromStaff = total.find(
                (emp) => emp.employeeId === user.userId
            );

            setProfileImage(
                user.workInformation?.image ||
                user.image ||
                currentUserFromStaff?.image ||
                null
            );
        }
    }, [user, total]);

    const getImageUrl = (path) => {
        if (!path) return null;

        if (path.startsWith("http") || path.startsWith("blob:")) {
            return path;
        }

        const BASE_URL = process.env.REACT_APP_API_URL;

        return `${BASE_URL}${path}`;
    };

    const today = new Date().toLocaleDateString("en-US", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
    });

    const params = new URLSearchParams(window.location.search);
    const currentTab = params.get("tab") || "dashboard";
    const showDashboardCalendar =
        ["/dashboard", "/admin", "/management", "/hr-dashboard", "/sales-dashboard"].includes(location)
        || (location === "/hr" && currentTab === "dashboard");

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 840);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target)) {
                setShowCalendar(false);
            }
        };
        if (showCalendar) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [showCalendar]);

    function formatMonth(date) {
        return date.toLocaleDateString("en-US", {
            month: "long",
            year: "numeric",
        });
    }

    if (!user) {
        return (
            <div className="flex items-center justify-between fixed z-[9] w-full mx-auto bg-[#fdf9fb] shadow-sm p-4 sm:p-6">
                <div>
                    <h1 className="text-lg sm:text-xl font-bold text-[#850002]">Welcome!</h1>
                    <p className="text-xs sm:text-sm text-gray-500">Today is {today}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed top-0 left-0 lg:left-64 right-0 z-[50] bg-[#fdf9fb] shadow-sm py-2 px-2 sm:py-3 sm:px-3">
            <div className="flex items-center justify-between w-full mx-auto">
                {/* Left side */}
                <div className="flex lg:block items-center lg:items-start gap-3">
                    <button
                        onClick={() => setOpen(true)}
                        className="lg:hidden p-0 sm:p-2 rounded-md hover:bg-gray-100"
                    >
                        <Menu className="w-6 h-6 text-gray-700" />
                    </button>
                    <div>
                        <h1 className="text-[14px] sm:text-[16px] lg:text-xl font-bold text-[#850002]">
                            Welcome {user?.fullName}!
                        </h1>
                        <p className="text-[10px] sm:text-[12px] lg:text-sm text-gray-500">
                            Today is {today}
                        </p>
                    </div>
                </div>

                {/* Right side */}
                <div className="flex items-center gap-2 sm:gap-4" ref={calendarRef}>
                    {showDashboardCalendar && (
                        <div className="relative hidden sm:flex items-center gap-2 bg-white border border-gray-200 rounded-xl px-3 py-2 shadow-sm">
                            {/* Date Display */}
                            <div
                                onClick={() => isMobile && setShowCalendar(!showCalendar)}
                                className="flex items-center gap-1 sm:gap-2 text-sm sm:text-[14px] text-gray-700 cursor-pointer hover:text-gray-900"
                            >
                                <Calendar className="w-4 h-4 text-gray-500" />
                                <span className="font-medium whitespace-nowrap">
                                    {rangeType === "monthly"
                                        ? formatMonth(start)
                                        : rangeType === "yearly"
                                            ? selectedYear
                                            : `${formatDate(start)} – ${formatDate(end)}`}
                                </span>
                            </div>

                            {!isMobile && (
                                <div className="flex items-center gap-1 sm:gap-2 border-l border-gray-200 pl-2 sm:pl-3">
                                    {rangeType === "monthly" && (
                                        <select
                                            value={selectedDate.getMonth()}
                                            onChange={(e) => {
                                                const newDate = new Date(selectedDate);
                                                newDate.setMonth(Number(e.target.value));
                                                setSelectedDate(newDate);
                                            }}
                                            className="px-1 sm:px-2 py-1 text-xs sm:text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            {Array.from({ length: 12 }, (_, i) => (
                                                <option key={i} value={i}>
                                                    {new Date(0, i).toLocaleString("en-US", { month: "long" })}
                                                </option>
                                            ))}
                                        </select>
                                    )}

                                    <select
                                        value={selectedYear}
                                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                                        className="px-1 sm:px-2 py-1 text-xs sm:text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {Array.from({ length: 2050 - 2024 + 1 }, (_, i) => 2024 + i).map(
                                            (year) => (
                                                <option key={year} value={year}>
                                                    {year}
                                                </option>
                                            )
                                        )}
                                    </select>

                                    <select
                                        value={rangeType}
                                        onChange={(e) => setRangeType(e.target.value)}
                                        className="px-1 sm:px-2 py-1 text-xs sm:text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                        <option value="yearly">Yearly</option>
                                    </select>
                                </div>
                            )}

                            {isMobile && showCalendar && (
                                <div className="absolute top-full left-0 mt-2 w-56 sm:w-64 p-3 bg-white border border-gray-200 rounded-xl shadow-xl z-[9999]">
                                    <select
                                        value={selectedYear}
                                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                                        className="w-full mb-2 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        {Array.from({ length: 2050 - 2024 + 1 }, (_, i) => 2024 + i).map(
                                            (year) => (
                                                <option key={year} value={year}>
                                                    {year}
                                                </option>
                                            )
                                        )}
                                    </select>

                                    {rangeType === "monthly" && (
                                        <select
                                            value={selectedDate.getMonth()}
                                            onChange={(e) => {
                                                const newDate = new Date(selectedDate);
                                                newDate.setMonth(Number(e.target.value));
                                                setSelectedDate(newDate);
                                            }}
                                            className="w-full mb-2 px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        >
                                            {Array.from({ length: 12 }, (_, i) => (
                                                <option key={i} value={i}>
                                                    {new Date(0, i).toLocaleString("en-US", { month: "long" })}
                                                </option>
                                            ))}
                                        </select>
                                    )}

                                    <select
                                        value={rangeType}
                                        onChange={(e) => setRangeType(e.target.value)}
                                        className="w-full px-3 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    >
                                        <option value="daily">Daily</option>
                                        <option value="weekly">Weekly</option>
                                        <option value="monthly">Monthly</option>
                                        <option value="yearly">Yearly</option>
                                    </select>
                                </div>
                            )}
                        </div>
                    )}

                    <Notifications />

                    <div className="hidden sm:flex items-center gap-2 sm:gap-3  px-2 sm:px-3 py-1 rounded-lg">
                        {profileImage ? (
                            <img
                                src={getImageUrl(profileImage)}
                                alt="profile"
                                className="w-9 h-9 rounded-full object-cover"
                            />
                        ) : (
                            <div className="w-8 sm:w-10 h-8 sm:h-10 rounded-full bg-gray-300 flex items-center justify-center text-xs sm:text-sm font-semibold text-gray-700 border">
                                {(user?.fullName || user?.username || "U")
                                    .split(" ")
                                    .map((n) => n[0])
                                    .slice(0, 2)
                                    .join("")
                                    .toUpperCase()}
                            </div>
                        )}
                        <div className="text-xs sm:text-sm">
                            <p className="font-semibold">{user?.fullName}</p>
                            <p className="text-[10px] sm:text-xs text-gray-500 capitalize">{user?.role}</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}